import {
  GeneralThemeFormValues,
  IGeneralThemeTimeFormat
} from 'components/Theme/GeneralSettings/generalTheme.types';

import { defaultStoreSettings } from './StoreSettings.consts';
import {
  ETimeAbbreviationStyle,
  ETimeBaseUnit,
  IQuantityFormat,
  IStoreSettingsFormValues,
  ITimeFormat
} from './StoreSettings.types';

const buildQuantityFormat = (
  source: Partial<IQuantityFormat> | undefined,
  fallback: IQuantityFormat
): IQuantityFormat => ({
  milSeparator: source?.milSeparator || fallback.milSeparator,
  fractionalSeparator: source?.fractionalSeparator || fallback.fractionalSeparator,
  thousandShorthand: source?.thousandShorthand ?? fallback.thousandShorthand,
  millionShorthand: source?.millionShorthand ?? fallback.millionShorthand,
  billionShorthand: source?.billionShorthand ?? fallback.billionShorthand,
  trillionShorthand: source?.trillionShorthand ?? fallback.trillionShorthand,
  quadrillionShorthand:
    source?.quadrillionShorthand ?? fallback.quadrillionShorthand,
  quintillionShorthand:
    source?.quintillionShorthand ?? fallback.quintillionShorthand,
  sextillionShorthand:
    source?.sextillionShorthand ?? fallback.sextillionShorthand,
  septillionShorthand:
    source?.septillionShorthand ?? fallback.septillionShorthand,
  digitsBasedAbbreviation:
    source?.digitsBasedAbbreviation ?? fallback.digitsBasedAbbreviation,
  maxDigitsWithoutShorthand:
    source?.maxDigitsWithoutShorthand ?? fallback.maxDigitsWithoutShorthand
});

const toBaseUnit = (
  value: string | undefined,
  fallback: ETimeBaseUnit
): ETimeBaseUnit => {
  const isKnownBaseUnit = Object.values(ETimeBaseUnit).includes(
    value as ETimeBaseUnit
  );
  return isKnownBaseUnit ? (value as ETimeBaseUnit) : fallback;
};

const toAbbreviationStyle = (
  value: string | undefined,
  fallback: ETimeAbbreviationStyle
): ETimeAbbreviationStyle => {
  const isKnownStyle = Object.values(ETimeAbbreviationStyle).includes(
    value as ETimeAbbreviationStyle
  );
  return isKnownStyle ? (value as ETimeAbbreviationStyle) : fallback;
};

const buildTimeFormat = (
  source: IGeneralThemeTimeFormat | undefined,
  fallback: ITimeFormat
): ITimeFormat => {
  let baseUnitThreshold = fallback.baseUnitThreshold;
  if (source?.baseUnitThreshold !== undefined) {
    baseUnitThreshold = source.baseUnitThreshold;
  }
  return {
    baseUnitThreshold,
    baseUnit: toBaseUnit(source?.baseUnit, fallback.baseUnit),
    abbreviationStyle: toAbbreviationStyle(
      source?.abbreviationStyle,
      fallback.abbreviationStyle
    )
  };
};

export const buildStoreSettingsInitialValues = (
  data?: GeneralThemeFormValues
): IStoreSettingsFormValues => ({
  productsQuantityFormat: buildQuantityFormat(
    data?.productsQuantityFormat,
    defaultStoreSettings.productsQuantityFormat
  ),
  balanceQuantityFormat: buildQuantityFormat(
    data?.balanceQuantityFormat,
    defaultStoreSettings.balanceQuantityFormat
  ),
  specialOfferQuantityFormat: buildQuantityFormat(
    data?.specialOfferQuantityFormat,
    defaultStoreSettings.specialOfferQuantityFormat
  ),
  rollingOfferQuantityFormat: buildQuantityFormat(
    data?.rollingOfferQuantityFormat,
    defaultStoreSettings.rollingOfferQuantityFormat
  ),
  progressBarQuantityFormat: buildQuantityFormat(
    data?.progressBarQuantityFormat,
    defaultStoreSettings.progressBarQuantityFormat
  ),
  triggeredPopupQuantityFormat: buildQuantityFormat(
    data?.triggeredPopupQuantityFormat,
    defaultStoreSettings.triggeredPopupQuantityFormat
  ),
  productsTimeFormat: buildTimeFormat(
    data?.productsTimeFormat,
    defaultStoreSettings.productsTimeFormat
  ),
  balanceTimeFormat: buildTimeFormat(
    data?.balanceTimeFormat,
    defaultStoreSettings.balanceTimeFormat
  ),
  specialOfferTimeFormat: buildTimeFormat(
    data?.specialOfferTimeFormat,
    defaultStoreSettings.specialOfferTimeFormat
  ),
  rollingOfferTimeFormat: buildTimeFormat(
    data?.rollingOfferTimeFormat,
    defaultStoreSettings.rollingOfferTimeFormat
  ),
  progressBarTimeFormat: buildTimeFormat(
    data?.progressBarTimeFormat,
    defaultStoreSettings.progressBarTimeFormat
  ),
  triggeredPopupTimeFormat: buildTimeFormat(
    data?.triggeredPopupTimeFormat,
    defaultStoreSettings.triggeredPopupTimeFormat
  )
});

export const applyQuantityFieldChange = (
  setFieldValue: (field: string, value: boolean | string) => void,
  fieldPath: string,
  rawValue: string
): void => {
  if (rawValue === 'true' || rawValue === 'false') {
    setFieldValue(fieldPath, rawValue === 'true');
    return;
  }
  setFieldValue(fieldPath, rawValue);
};
